import type { SyntheticEvent } from "react"
import _t from "@core/i18n"
import { navigate } from "gatsby"
import get from "lodash/get"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import useProductAddToBasket from "~/hooks/useProductAddToBasket"
import useSaleConfigurationState from "~/hooks/useSaleConfigurationState"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { getSelectedPlanSettings } from "~/utils"

export type PrimaryButtonHook = {
  text: string
  title: string
  href?: string
  onClick?: { (event: SyntheticEvent): void }
  isLoading?: boolean
}

export function usePrimaryButton(
  saleConfiguration: SaleConfiguration
): PrimaryButtonHook | null {
  const { currentPriceType } = usePriceTypeContext()

  const { options } = useSaleConfigurationState(saleConfiguration.id)

  const { isLoading, addProduct } = useProductAddToBasket(saleConfiguration.id)

  const selectedPlanSettings = getSelectedPlanSettings(
    saleConfiguration.flatData.optionsMapping || [],
    options
  )

  const period = selectedPlanSettings?.period?.period

  const productData: CloudBluePlan["flatData"] | undefined = get(
    selectedPlanSettings,
    "cloudBluePlan[0].flatData"
  )

  const defaultProductData: CloudBluePlan["flatData"] | undefined = get(
    saleConfiguration,
    "flatData.defaultCloudBluePlan[0].flatData"
  )

  const currentProductData = productData || defaultProductData

  if (isEmpty(currentProductData)) {
    return null
  }

  const onSuccess = () => {
    navigate(url.getBasketUrl())
  }

  if (saleConfiguration.flatData.skipBonusScreen) {
    return {
      text: _t("marketplace.chooseButton"),
      title: _t("marketplace.chooseButton"),
      onClick: () => {
        addProduct(currentProductData, onSuccess)
      },
      isLoading,
    }
  }

  return {
    text: _t("marketplace.chooseButton"),
    title: _t("marketplace.chooseButton"),
    href: url.generateBonusUrl({
      p: currentProductData.alias,
      ax: getUpsellData(saleConfiguration.flatData.upsell),
      period,
      priceType: currentPriceType,
    }),
  }
}
